import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

type TestItem = {
  id: string;
  name: string;
  whatToDo: string;
  passLabel: string;
  failLabel: string;
};

type PlanContent = {
  title: string;
  scope: string;
  prerequisites: string[];
  steps: { title: string; body: string }[];
  tests: TestItem[];
};

const plan1: PlanContent = {
  title: 'Plan 1: Reporting Correctness (Views + Cross-Report Numbers)',
  scope:
    'Ensure SQL reporting views return data for completed jobs, and that revenue/job counts align across RPC, Overall Report, and Finance Report for the same period.',
  prerequisites: [
    'Migrations applied: 20260304000000_fix_reporting_views_status_completed.sql (views use status = \'completed\').',
    'You have at least one completed job in the database.',
    'Access to Supabase SQL Editor and to the app Reports page with a date range that includes completed jobs.',
  ],
  steps: [
    {
      title: 'Step 1: Confirm reporting views migration is applied',
      body:
        'In Supabase Dashboard → SQL Editor, run: SELECT definition FROM pg_views WHERE schemaname = \'public\' AND viewname = \'view_report_financial_pnl\'; Confirm the definition contains j.status = \'completed\' and not \'closed\'.',
    },
    {
      title: 'Step 2: Verify views return rows for completed jobs',
      body:
        'Run: SELECT COUNT(*) FROM view_report_financial_pnl; and same for view_report_technician_performance, view_report_data_hygiene. With completed jobs, financial_pnl and technician_performance should return at least one row.',
    },
    {
      title: 'Step 3–7: Pick date range, record RPC + Overall + Finance numbers, compare, lead count check',
      body:
        'Choose one date range and timezone. Record get_revenue_summary(start_date, end_date) and Overall/Finance report totals. Compare; document any divergence. Check lead counts match across Dashboard and Reports.',
    },
  ],
  tests: [
    { id: '1.1', name: 'Test 1.1: Views use status completed', whatToDo: 'Run SQL to check view definition contains status = \'completed\'.', passLabel: 'Definition shows \'completed\'', failLabel: 'Definition still has \'closed\' or view missing' },
    { id: '1.2', name: 'Test 1.2: view_report_financial_pnl returns data', whatToDo: 'Run: SELECT * FROM view_report_financial_pnl LIMIT 5;', passLabel: 'Rows returned (or 0 rows and no completed jobs)', failLabel: 'Have completed jobs but 0 rows' },
    { id: '1.3', name: 'Test 1.3: Same-period revenue comparison', whatToDo: 'Compare get_revenue_summary vs Overall vs Finance for same date range.', passLabel: 'All three match (or within rounding)', failLabel: 'Revenue or job count differs significantly' },
    { id: '1.4', name: 'Test 1.4: Lead count consistency', whatToDo: 'Same range: note lead count on Dashboard and Reports. Should match.', passLabel: 'Lead counts match', failLabel: 'Counts differ' },
  ],
};

const plan2: PlanContent = {
  title: 'Plan 2: Linkage Integrity (Lead–Job + Boards)',
  scope:
    'Ensure lead–job bidirectional links are correct (no orphan jobs, no broken back-links, converted leads have job_id), and that Boards have consistent company_id and valid pipeline/status.',
  prerequisites: [
    'Migrations applied (including 20260304000001_data_integrity_checks_rpc.sql).',
    'Access to Supabase SQL Editor to run scripts and see failing rows.',
  ],
  steps: [
    { title: 'Step 1: Run integrity RPC and record baseline', body: 'Call run_data_integrity_checks(); record lead_job and boards counts.' },
    { title: 'Step 2: Run 01_lead_job_linkage.sql', body: 'Run all four SELECTs; note orphan jobs, broken back-links, converted without job_id, job without lead back-link.' },
    { title: 'Step 3: Fix lead–job issues', body: 'Fix or document orphan jobs, broken back-links, converted_no_job, job_no_lead_backlink. Re-run until counts are 0.' },
    { title: 'Step 4: Run 04_boards_integrity.sql', body: 'Fix company_id mismatch, invalid status, invalid pipeline. Re-run until 0.' },
    { title: 'Step 5: Confirm RPC ok for linkage and boards', body: 'run_data_integrity_checks() shows lead_job and boards all zeros.' },
  ],
  tests: [
    { id: '2.1', name: 'Test 2.1: RPC runs and returns structure', whatToDo: 'Call run_data_integrity_checks(); check keys: lead_job, boards, ok.', passLabel: 'RPC returns JSON with all keys', failLabel: 'Error or missing keys' },
    { id: '2.2', name: 'Test 2.2: No orphan jobs', whatToDo: 'First query in 01_lead_job_linkage.sql. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '2.3', name: 'Test 2.3: No broken back-links', whatToDo: 'Second query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '2.4', name: 'Test 2.4: No converted without job_id', whatToDo: 'Third query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '2.5', name: 'Test 2.5: No job without lead back-link', whatToDo: 'Fourth query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '2.6', name: 'Test 2.6: Boards – no company_id mismatch', whatToDo: 'First query in 04_boards_integrity.sql. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '2.7', name: 'Test 2.7: Boards – no invalid status or pipeline', whatToDo: 'Second and third queries. Expect 0 rows.', passLabel: '0 rows for both', failLabel: 'Rows returned' },
    { id: '2.8', name: 'Test 2.8: Full RPC ok for linkage and boards', whatToDo: 'run_data_integrity_checks(); lead_job and boards all zeros.', passLabel: 'All zeros', failLabel: 'Non-zero' },
  ],
};

const plan3: PlanContent = {
  title: 'Plan 3: Aggregates and Job Dates (Customers + closed_at / close_date)',
  scope:
    'Ensure customer total_jobs_count, total_leads_count, total_lifetime_value match actuals; completed jobs have closed_at/close_date set and consistent; UI reflects correct numbers.',
  prerequisites: [
    'Migrations applied (triggers for customer totals).',
    'Access to Supabase SQL Editor and app Customers + Reports pages.',
  ],
  steps: [
    { title: 'Step 1: Run 02_customer_aggregates.sql', body: 'Note jobs_count_mismatch, leads_count_mismatch, ltv_mismatch.' },
    { title: 'Step 2: Fix customer aggregates', body: 'Update totals from actual counts/sums or fix triggers. Re-run until 0 rows.' },
    { title: 'Step 3: Run 03_jobs_date_integrity.sql', body: 'Note completed_null_closed_at, completed_null_close_date, closed_at_close_date_mismatch.' },
    { title: 'Step 4: Fix job dates', body: 'Backfill close_date from closed_at where null; fix mismatches. Re-run until 0.' },
    { title: 'Step 5: Verify in app', body: 'Spot-check Customers page and Reports; confirm numbers correct.' },
    { title: 'Step 6: Confirm RPC', body: 'run_data_integrity_checks(); customer_aggregates and jobs_dates all zeros.' },
  ],
  tests: [
    { id: '3.1', name: 'Test 3.1: Customer total_jobs_count matches actual', whatToDo: 'First query in 02_customer_aggregates.sql. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '3.2', name: 'Test 3.2: Customer total_leads_count matches actual', whatToDo: 'Second query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '3.3', name: 'Test 3.3: Customer total_lifetime_value matches actual', whatToDo: 'Third query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '3.4', name: 'Test 3.4: No completed jobs with null closed_at', whatToDo: 'First query in 03_jobs_date_integrity.sql. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '3.5', name: 'Test 3.5: No completed jobs with null close_date', whatToDo: 'Second query. Expect 0 rows.', passLabel: '0 rows', failLabel: 'Rows returned' },
    { id: '3.6', name: 'Test 3.6: No closed_at vs close_date mismatch', whatToDo: 'Third query. Ideally 0 rows.', passLabel: '0 rows', failLabel: 'Unexpected mismatches' },
    { id: '3.7', name: 'Test 3.7: UI spot-check – customer counts and LTV', whatToDo: 'Customers page: confirm 2–3 customers match DB.', passLabel: 'Numbers match', failLabel: 'Mismatch' },
    { id: '3.8', name: 'Test 3.8: Full RPC ok for aggregates and job dates', whatToDo: 'run_data_integrity_checks(); customer_aggregates and jobs_dates zeros.', passLabel: 'All zeros, ok = true', failLabel: 'Non-zero' },
  ],
};

function PlanTabContent({ plan, feedbackKey }: { plan: PlanContent; feedbackKey: string }) {
  const [feedback, setFeedback] = useState<Record<string, { pass: boolean; fail: boolean; notes: string }>>(() => {
    const init: Record<string, { pass: boolean; fail: boolean; notes: string }> = {};
    plan.tests.forEach((t) => {
      init[`${feedbackKey}-${t.id}`] = { pass: false, fail: false, notes: '' };
    });
    return init;
  });

  const updateFeedback = (id: string, field: 'pass' | 'fail' | 'notes', value: boolean | string) => {
    const key = `${feedbackKey}-${id}`;
    setFeedback((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...(typeof value === 'string' ? { notes: value } : field === 'pass' ? { pass: value, fail: false } : { fail: value, pass: false }),
      },
    }));
  };

  return (
    <ScrollArea className="h-[calc(100vh-280px)] min-h-[400px] pr-4">
      <div className="space-y-6 pb-6">
        <Card>
          <CardHeader>
            <CardTitle>{plan.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{plan.scope}</p>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Prerequisites</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              {plan.prerequisites.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Implementation Steps</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {plan.steps.map((step, i) => (
              <div key={i}>
                <p className="font-medium text-sm">{step.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{step.body}</p>
                {i < plan.steps.length - 1 && <Separator className="mt-3" />}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Action Plan: Tests (with your feedback)</CardTitle>
            <p className="text-sm text-muted-foreground">Run each test and mark Pass/Fail with notes below.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {plan.tests.map((test) => {
              const key = `${feedbackKey}-${test.id}`;
              const state = feedback[key] ?? { pass: false, fail: false, notes: '' };
              return (
                <div key={test.id} className="rounded-lg border p-4 space-y-3">
                  <p className="font-medium text-sm">{test.name}</p>
                  <p className="text-sm text-muted-foreground">{test.whatToDo}</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={state.pass}
                        onCheckedChange={(c) => updateFeedback(test.id, 'pass', !!c)}
                      />
                      Pass – {test.passLabel}
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={state.fail}
                        onCheckedChange={(c) => updateFeedback(test.id, 'fail', !!c)}
                      />
                      Fail – {test.failLabel}
                    </label>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Your feedback / notes</Label>
                    <Textarea
                      placeholder="Notes for this test…"
                      className="min-h-[60px] text-sm"
                      value={state.notes}
                      onChange={(e) => updateFeedback(test.id, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  );
}

export default function ImplementationPlans() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Three implementation plans for data integrity. Complete the steps in each tab and record your test feedback below.
      </p>
      <Tabs defaultValue="plan1" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="plan1">Plan 1: Reporting</TabsTrigger>
          <TabsTrigger value="plan2">Plan 2: Linkage</TabsTrigger>
          <TabsTrigger value="plan3">Plan 3: Aggregates & Dates</TabsTrigger>
        </TabsList>
        <TabsContent value="plan1" className="mt-4">
          <PlanTabContent plan={plan1} feedbackKey="plan1" />
        </TabsContent>
        <TabsContent value="plan2" className="mt-4">
          <PlanTabContent plan={plan2} feedbackKey="plan2" />
        </TabsContent>
        <TabsContent value="plan3" className="mt-4">
          <PlanTabContent plan={plan3} feedbackKey="plan3" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
